import { api } from './api';

/**
 * Server-side messaging shapes (mirror the Prisma models in
 * apps/api/src/modules/messaging). v1: chambers staff only, plain text.
 */

export type ThreadKind = 'matter_internal' | 'matter_external' | 'chambers_internal' | 'cross_chambers';
export type PrivilegeMarker = 'none' | 'legal_advice' | 'without_prejudice';
export type ParticipantRole = 'member' | 'owner' | 'observer';
export type MessageType = 'general' | 'urgent' | 'instruction' | 'hearing_update' | 'fee_discussion';

export const MESSAGE_TYPE_LABELS: Record<MessageType, string> = {
  general:        'General',
  urgent:         'Urgent',
  instruction:    'Instruction',
  hearing_update: 'Hearing update',
  fee_discussion: 'Fee discussion',
};

export const MESSAGE_TYPE_COLOURS: Record<MessageType, string> = {
  general:        '',
  urgent:         'text-red-700 dark:text-red-300 bg-red-50 border-red-200',
  instruction:    'text-brand-700 dark:text-brand-300 bg-brand-50 border-brand-200',
  hearing_update: 'text-amber-700 dark:text-amber-300 bg-amber-50 border-amber-200',
  fee_discussion: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 border-emerald-200',
};

export interface ThreadParticipantUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  memberType: string;
  /** The participant's home chambers — differs from the caller's for a cross_chambers corridor. */
  chambersId?: string;
}

export interface ThreadParticipant {
  id: string;
  userId: string;
  role: ParticipantRole;
  lastReadSeq: number;
  joinedAt: string;
  /** The participant's home chambers/workspace. */
  chambersId?: string;
  user: ThreadParticipantUser;
}

export interface MessageThread {
  id: string;
  chambersId: string;
  matterId: string | null;
  kind: ThreadKind;
  subject: string | null;
  privilege: PrivilegeMarker;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  lastMessageAt: string | null;
  case: { id: string; caseNumber: string; caseName: string } | null;
  participants: ThreadParticipant[];
  /** Number of unread messages for the current user */
  unreadCount?: number;
  /** Last message preview for thread list */
  messages?: Array<{
    id: string;
    body: string;
    senderUserId: string | null;
    senderUser: { id: string; firstName: string; lastName: string } | null;
  }>;
}

export interface MessageSenderUser {
  id: string;
  firstName: string;
  lastName: string;
  memberType: string;
}

export interface MessageAttachment {
  id: string;
  documentId: string;
  document: {
    id: string;
    name: string;
    mimeType: string;
    fileSizeBytes: number | string;
    fileUrl: string | null;
  };
}

export interface ChatMessage {
  id: string;
  threadId: string;
  senderUserId: string | null;
  body: string;
  messageType: MessageType;
  sequence: number;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  pinnedAt: string | null;
  pinnedById: string | null;
  senderUser: MessageSenderUser | null;
  pinnedBy: { id: string; firstName: string; lastName: string } | null;
  seenByOthers?: boolean;
  attachments?: MessageAttachment[];
}

export async function fetchThreads(opts?: {
  matterId?: string;
  kind?: ThreadKind;
  search?: string;
  limit?: number;
}): Promise<MessageThread[]> {
  const params: Record<string, string> = {};
  if (opts?.matterId) params.matterId = opts.matterId;
  if (opts?.kind) params.kind = opts.kind;
  if (opts?.search) params.search = opts.search;
  if (opts?.limit) params.limit = String(opts.limit);
  const res = await api.get<{ threads: MessageThread[] }>('/messaging/threads', {
    params,
  });
  return res.data.threads;
}

export async function fetchThread(id: string): Promise<MessageThread> {
  const res = await api.get<{ thread: MessageThread }>(`/messaging/threads/${id}`);
  return res.data.thread;
}

export async function fetchThreadUnreadCount(): Promise<number> {
  const res = await api.get<{ count: number }>('/messaging/threads/unread-count');
  return res.data.count;
}

export async function fetchMessages(
  threadId: string,
  opts?: { beforeSequence?: number; limit?: number },
): Promise<ChatMessage[]> {
  const params: Record<string, string> = {};
  if (opts?.beforeSequence) params.beforeSequence = String(opts.beforeSequence);
  if (opts?.limit) params.limit = String(opts.limit);
  const res = await api.get<{ messages: ChatMessage[] }>(
    `/messaging/threads/${threadId}/messages`,
    { params },
  );
  return res.data.messages;
}

export interface CreateThreadPayload {
  kind: ThreadKind;
  matterId?: string | null;
  subject?: string | null;
  participantUserIds: string[];
  initialMessage?: string;
  privilege?: PrivilegeMarker;
}

export async function createThread(payload: CreateThreadPayload): Promise<MessageThread> {
  const res = await api.post<{ thread: MessageThread }>('/messaging/threads', payload);
  return res.data.thread;
}

export async function sendMessage(
  threadId: string,
  text: string,
  opts?: { messageType?: MessageType; documentIds?: string[] },
): Promise<ChatMessage> {
  const res = await api.post<{ message: ChatMessage }>(
    `/messaging/threads/${threadId}/messages`,
    { text, messageType: opts?.messageType ?? 'general', documentIds: opts?.documentIds },
  );
  return res.data.message;
}

export interface SearchResult {
  threads: MessageThread[];
  messages: Array<{
    id: string;
    threadId: string;
    body: string;
    senderUserId: string | null;
    sequence: number;
    createdAt: string;
    messageType: MessageType;
    senderUser: { id: string; firstName: string; lastName: string; memberType: string } | null;
  }>;
}

export async function searchMessaging(
  q: string,
  opts?: { matterId?: string; limit?: number },
): Promise<SearchResult> {
  const params: Record<string, string> = { q };
  if (opts?.matterId) params.matterId = opts.matterId;
  if (opts?.limit) params.limit = String(opts.limit);
  const res = await api.get<SearchResult>('/messaging/search', { params });
  return res.data;
}

export async function markThreadRead(
  threadId: string,
  upToSequence: number,
): Promise<void> {
  await api.post(`/messaging/threads/${threadId}/read`, { upToSequence });
}

export async function addThreadParticipant(
  threadId: string,
  userId: string,
): Promise<void> {
  await api.post(`/messaging/threads/${threadId}/participants`, { userId });
}

export async function removeThreadParticipant(
  threadId: string,
  userId: string,
  reason?: string,
): Promise<void> {
  await api.delete(`/messaging/threads/${threadId}/participants/${userId}`, {
    data: reason ? { reason } : undefined,
  });
}

export async function leaveThread(threadId: string): Promise<void> {
  await api.post(`/messaging/threads/${threadId}/leave`);
}

export async function pinMessage(
  threadId: string,
  messageId: string,
): Promise<ChatMessage> {
  const res = await api.post<{ message: ChatMessage }>(
    `/messaging/threads/${threadId}/messages/${messageId}/pin`,
  );
  return res.data.message;
}

export async function unpinMessage(
  threadId: string,
  messageId: string,
): Promise<void> {
  await api.delete(`/messaging/threads/${threadId}/messages/${messageId}/pin`);
}

export async function fetchPinnedMessages(
  threadId: string,
): Promise<ChatMessage[]> {
  const res = await api.get<{ messages: ChatMessage[] }>(
    `/messaging/threads/${threadId}/pinned`,
  );
  return res.data.messages;
}

// ─── Cross-workspace barrister DM ───────────────────────────────

export interface CrossChambersBarrister {
  userId: string;
  firstName: string;
  lastName: string;
  memberType: string;
  workspaceName: string;
  workspaceType: string;
}

/**
 * Resolve a verified email to an eligible barrister in ANOTHER workspace. Returns null when there is
 * no eligible match (or the email belongs to your own chambers) — no account-enumeration signal.
 * Email is used ONLY to identify the platform account; the message never travels by email.
 */
export async function resolveCrossBarrister(email: string): Promise<CrossChambersBarrister | null> {
  const res = await api.post<{ barrister: CrossChambersBarrister | null }>(
    '/messaging/cross-chambers/resolve',
    { email },
  );
  return res.data.barrister;
}

/** Create (or reuse) a cross-workspace DM corridor with the resolved barrister. */
export async function createCrossThread(
  recipientUserId: string,
  initialMessage?: string,
): Promise<MessageThread> {
  const res = await api.post<{ thread: MessageThread }>('/messaging/cross-chambers', {
    recipientUserId,
    ...(initialMessage ? { initialMessage } : {}),
  });
  return res.data.thread;
}
